import { Component, Mixins } from 'vue-property-decorator';
import Cookies from 'js-cookie';
import { getComponentProps } from '@/utils/vueUtils';
import { getFormPayload, getResizeImage, toggleModalClassListWithoutTouchMove } from '@/utils/commonUtils';

import ButtonClose from '@/components/ButtonClose/ButtonClose.vue';
import { isInUSTimezone } from 'in-us'
import { NewsletterSubscriptionPopupViewModel } from './NewsletterPopup.types';
import NewsletterPopupForm from '@/modules/NewsletterPopup/NewsletterPopupForm/NewsletterPopupForm.vue';
import StoreModuleMixin from '@/mixins/StoreModuleMixin';
import { submitEmailSignUp } from '../EmailSignUpModule/EmailSignUpModule.api';
import ResponsiveImage from '@/components/ResponsiveImage/ResponsiveImage.vue';
import { ImageSizes, ImageSize } from '@/common/interfaces/SizeBreakpoints';
import { SegmentElement, SegmentTrackEvents } from '@/plugins/utm/SegmentElement';
import RecaptchaMixin from '@/forms/FormElements/Mixins/RecaptchaMixin';
import { getStore, shouldShowOptIn } from '@/store';
import { Country } from '@/store/modules/metadataModule';

@Component({
  props: getComponentProps<NewsletterSubscriptionPopupViewModel>({
    TriggerTime: 30000,
    ShowOnFirstPage: false,
    SuppressPopup: false,
    Title: 'Be the first to get Special Offers, expeditions tips and travel stories',
    SubTitle: 'Subscribe to our weekly newsletter and get a free brochure',
    ButtonText: 'send me offers',
    BottomText: 'I authorize Lindblad Expeditions to email me; however, I am able to unsubscribe at any time. For more details, see our <a "href="#" target="_blank" class="NewsletterPopup__link">Privacy Policy</a>.',
    LandscapeImage: {
      Url: getResizeImage('LandscapeImage.jpg'),
      Alt: 'Photographer',
    },
    PortraitImage: {
      Url: getResizeImage('PortraitImage.jpg'),
      Alt: 'Photographer',
    },
    ThankYouText: 'Thank you'
  }),
  components: {
    NewsletterPopupForm,
    ButtonClose,
    ResponsiveImage,
  }
})

export default class NewsletterPopup extends Mixins(StoreModuleMixin, RecaptchaMixin) {
  readonly p!: NewsletterSubscriptionPopupViewModel;

  isPopupVisible: Boolean = false;
  formSent: Boolean = false;
  formName: string = '';
  isUSCountry = true;
  
  imageSizes: ImageSizes = {
    def: new ImageSize([1920, 1080]),
    sizes: {
      sm: new ImageSize([1920, 1080]),
      md: new ImageSize([1920, 1080]),
      lg: new ImageSize([1199, 900]),
      xl: new ImageSize([1920, 1080]),
    },
  };

  close() {
    document.body.classList.remove('overlay-noscroll');

    this.$emit('close');
    this.isPopupVisible = false;
    this.hideCaptcha();

    if (!this.formSent) {
      Cookies.set('newsletterPopupClosed', true, { expires: 3 });
    }
  }

  handleOnKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.close()
    }
  }

  openPopup(time = this.p.TriggerTime * 1000) {
    setTimeout(() => {
      const activePopup = document.querySelectorAll('.NewsLetterPopupMask.animate-active').length
      if (!activePopup) {
        document.body.classList.add('overlay-noscroll');
        this.isPopupVisible = true;
        this.showCaptcha();
        SegmentElement.sendEvent('formViewed', {
          ...getFormPayload(this.formName, this.$attrs.contentLinkId),
          nonInteraction: 1,
        })
      }
    }, time);
  }

  created() {
    if (this.p.TriggerTime > 0 && !this.p.SuppressPopup) {
      this.siteKey = '6Lelwv4eAAAAAHavTU2624NBQb8SDFnhmZzpvgZH';
    }
  }

  mounted() {
    this.hideCaptcha()
    this.formName = this.p.GateName || 'NewsletterSubscriptionPopup';

    const { Country: CountryId } = getStore().state.Meta;
    this.isUSCountry = CountryId === Country.US;

    if (this.p.SuppressPopup) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);

    const utmMedium = urlParams.get('utm_medium');
    const utmSource = urlParams.get('utm_source')?.toLowerCase();

    const insider = urlParams.get('insider')
    if (insider !== null) {
      return
    }

    // Mimic the logic from BE
    if (utmMedium === 'email' || utmSource === 'travelzoo' || utmSource === 'adestra') {
      Cookies.set('newsletterPopupClosed', true, { expires: 365 });
    }
    const newsletterPopupClosed = Cookies.get('newsletterPopupClosed');
    const newsletterSubscribed = Cookies.get('newsletterSubscribed');
    if (this.p.GateName && !newsletterSubscribed) {
      this.openPopup(0);
    }
    else if (!newsletterSubscribed && !newsletterPopupClosed && this.p.TriggerTime > 0 && this.hasInteractedWithConsentManager()) {
      this.openPopup();
    }
  }

  hasInteractedWithConsentManager() {
    const isUSA = isInUSTimezone()
    if (isUSA) return true
    return Cookies.get('tracking-preferences') || Cookies.get('tracking-preferences-stg')
  }

  async postForm(model) {
    model.RecaptchaResponse = await this.getRecaptchaToken()
    const payload = getFormPayload(this.formName, this.$attrs.contentLinkId);
    SegmentElement.sendEvent('formSubmissionAttempted', payload)
    try {
      const referring_domain = Cookies.get('referring_domain')
      const identifyPayload = {
        first_name: model.FirstName,
        last_name: model.LastName,
        email: model.Email
      }
      var serverPayload = {
        ...payload,
        ...(!SegmentElement.getIsLanding() && { previous_page_name: SegmentElement.getPrevPageName() }),
        page_name: SegmentElement.getPageName(),
        locale: SegmentElement.getLocale(),
        ...identifyPayload,
        ...this.p.AnalyticsData,
        ...(referring_domain && { referring_domain }),
        ...(model.PostalCode && { address: { postal_code: model.PostalCode } }),
        ...(Cookies.get('_fbc') && { fbclid: Cookies.get('_fbc').split('.')[3] }),
        ...(Cookies.get('email_md5') && { email_md5: Cookies.get('email_md5') }),
        ...(Cookies.get('_uetmsclkid') && { mscklid: Cookies.get('_uetmsclkid').split('_uet')[1] }),
        ...(Cookies.get('_gcl_aw') && { gclid: Cookies.get('_gcl_aw').split('.')[2] }),
        ...(shouldShowOptIn() && { eea_uk_optin: true }),
      }
      const data = { ...model, AnalyticsPayload: JSON.stringify(serverPayload), ...(this.p.GateName && { GateType: 'Brochure' }), }
      const response = await submitEmailSignUp(data);
      this.formSent = true;
      (payload as any).transaction_id = response.Item.transaction_id
      SegmentElement.sendEvent(SegmentTrackEvents.formSubmitted, payload)
      this.$emit('submit')
    } catch (error) {
      console.error(error);
    }
  }

  get ButtonText() {
    if (this.p.GateName) {
      return `Download Brochure`
    }
    return this.p.ButtonText
  }
  get BottomText() {
    if (this.p.GateName) {
      return this.p.BottomText.replace(/send me offers/i, this.ButtonText)
    }
    return this.p.BottomText
  }
}
